<template>
	<div
		class="g-block-audio-library"
		:class="{
			'g-block-audio-library--edit': editable,
			'is-active': editable && active,
			'g-block-audio-library--h5': h5,
			'g-block-audio-library--card': blockStyle.showCard,
		}"
		@click="onWrapClick"
	>
		<div
			class="g-block-audio-library__card"
			:style="cardStyle"
		>
			<div class="g-block-audio-library__header">
				<span
					class="g-block-audio-library__accent"
					:style="{ background: normalizedData.accentColor }"
				></span>
				<span
					class="g-block-audio-library__title"
					:style="{ color: normalizedData.titleColor }"
				>{{ normalizedData.title }}</span>
			</div>

			<div
				class="g-block-audio-library__list"
				:style="{ gap: blockStyle.colGap }"
			>
				<div
					v-for="item in items"
					:key="item.id"
					class="g-block-audio-library__item"
					@click="onWrapClick"
				>
					<div
						class="g-block-audio-library__cover"
						:style="coverStyle"
					>
						<img
							v-if="getCoverUrl(item)"
							:src="getCoverUrl(item)"
							alt=""
						/>
						<i v-else class="mbm-iconfont mbm-yinpin"></i>
						<span
							v-if="shouldShowPlayIcon"
							class="g-block-audio-library__play"
							:class="{ 'is-playing': isItemPlaying(item) }"
							role="button"
							:aria-label="isItemPlaying(item) ? '暂停' : '播放'"
							@click="onItemClick(item, $event)"
						>
							<span class="g-block-audio-library__triangle"></span>
							<span class="g-block-audio-library__pause">
								<span class="g-block-audio-library__pause-bar"></span>
								<span class="g-block-audio-library__pause-bar"></span>
							</span>
						</span>
					</div>
					<div
						class="g-block-audio-library__name"
						:style="{ color: normalizedData.nameColor }"
					>
						{{ getItemName(item) }}
					</div>
				</div>
			</div>
		</div>

		<audio
			v-if="playingUrl"
			ref="playerEl"
			:src="activeAudioSrc"
			@ended="onPlayEnded"
			@pause="onAudioPause"
			@play="onAudioPlay"
		></audio>
	</div>
</template>

<script>
	import baseFunc from '../../../../config/base_func.js';
	import {
		getAudioLibraryAudioUrl,
		getAudioLibraryBlockStyle,
		getAudioLibraryCoverStyle,
		getAudioLibraryCoverUrl,
		getAudioLibraryItemName,
		normalizeAudioLibraryData,
	} from '../../../utils/audioLibrary.js';

	export default {
		name: 'GAudioLibraryShell',
		props: {
			data: {
				type: Object,
				required: true,
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
			showPlayIcon: {
				type: Boolean,
				default: null,
			},
			idKey: {
				type: String,
				default: '',
			},
		},
		emits: ['select'],
		data() {
			return {
				playingUrl: '',
				playingItemId: '',
				isPlaying: false,
				permissionedItemId: '',
				permissionedPlayingUrl: '',
			};
		},
		computed: {
			normalizedData() {
				return normalizeAudioLibraryData(this.data);
			},
			items() {
				return this.normalizedData.items;
			},
			blockStyle() {
				return getAudioLibraryBlockStyle(this.data, { h5: this.h5 });
			},
			coverStyle() {
				return getAudioLibraryCoverStyle(this.data, { h5: this.h5 });
			},
			cardStyle() {
				if (!this.blockStyle.showCard) {
					return {};
				}
				return {
					borderRadius: this.blockStyle.cardBorderRadius,
					padding: this.blockStyle.cardPadding,
				};
			},
			isPlayable() {
				return this.editable || this.h5;
			},
			shouldShowPlayIcon() {
				return this.showPlayIcon === null ? this.isPlayable : this.showPlayIcon;
			},
			activeAudioSrc() {
				if (this.permissionedItemId === this.playingItemId && this.permissionedPlayingUrl) {
					return this.permissionedPlayingUrl;
				}
				return this.playingUrl;
			},
		},
		beforeUnmount() {
			this.stopPlay();
		},
		methods: {
			getCoverUrl(item) {
				return getAudioLibraryCoverUrl(item);
			},
			getItemName(item) {
				return getAudioLibraryItemName(item);
			},
			onWrapClick(e) {
				if (!this.editable) return;
				e?.stopPropagation?.();
				this.$emit('select');
			},
			isItemPlaying(item) {
				return this.isPlaying && this.playingItemId === item.id;
			},
			onItemClick(item, e) {
				if (!this.isPlayable) return;
				e?.stopPropagation?.();
				const url = getAudioLibraryAudioUrl(item);
				if (!url) return;
				if (this.isItemPlaying(item)) {
					this.pause();
					return;
				}
				this.playingItemId = item.id;
				this.playingUrl = url;
				this.isPlaying = true;
				this.startPlayback(item);
			},
			startPlayback(item) {
				// 编辑器画布：直接播放（无需鉴权）
				if (!this.h5) {
					this.$nextTick(() => {
						this.playAudioElement();
					});
					return;
				}
				// H5：先请求播放权限拿到签名 URL，再播放
				if (this.permissionedItemId === item.id && this.permissionedPlayingUrl) {
					this.$nextTick(() => {
						this.playAudioElement();
					});
					return;
				}
				this.requestPlayPermission(item)
					.then(() => {
						this.$nextTick(() => {
							this.playAudioElement();
						});
					})
					.catch(err => {
						this.$message?.error?.(err?.data?.message || '请求失败');
						this.stopPlay();
					});
			},
			playAudioElement() {
				const el = this.$refs.playerEl;
				if (!el) return;
				el.currentTime = 0;
				const playPromise = el.play?.();
				if (playPromise && typeof playPromise.catch === 'function') {
					playPromise.catch(() => {
						this.stopPlay();
					});
				}
			},
			requestPlayPermission(item) {
				return this.$apiRequest.post('/api/getMediaPlayPermissions', {
					attach_id: item?.audio?.id,
					id_key: this.idKey,
					unique: '',
					app_type:1,
					p_key: this.getQueryStringByChannel('p_key') == null ? '' : this.getQueryStringByChannel('p_key'),
				}).then(res => {
					const signedUrl = res?.data?.play_url;
					if (!signedUrl) {
						return Promise.reject({ data: { message: '未获取到播放地址' } });
					}
					this.permissionedItemId = item.id;
					this.permissionedPlayingUrl = baseFunc.getQiniusign(signedUrl);
				});
			},
			getQueryStringByChannel(name) {
				const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i');
				if (!window.location.href.split('?')[1]) {
					return null;
				}
				const re = window.location.href.split('?')[1].match(reg);
				if (re != null) return unescape(re[2]);
				return null;
			},
			onPlayEnded() {
				this.stopPlay();
			},
			onAudioPause() {
				if (!this.$refs.playerEl) return;
				this.isPlaying = false;
			},
			onAudioPlay() {
				this.isPlaying = true;
			},
			pause() {
				const el = this.$refs.playerEl;
				if (el) {
					el.pause();
				}
				this.isPlaying = false;
			},
			stopPlay() {
				const el = this.$refs.playerEl;
				if (el) {
					el.pause();
					el.currentTime = 0;
				}
				this.playingUrl = '';
				this.playingItemId = '';
				this.isPlaying = false;
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-audio-library {
		width: 100%;
		box-sizing: border-box;

		&--edit {
			border: 1px solid transparent;
			border-radius: 4px;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
			}
		}
	}

	.g-block-audio-library__card {
		box-sizing: border-box;
		width: 100%;

		.g-block-audio-library--card & {
			background: #fff;
			box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
		}
	}

	.g-block-audio-library__header {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 12px;

		.g-block-audio-library--h5 & {
			gap: 0.16rem;
			margin-bottom: 0.24rem;
		}
	}

	.g-block-audio-library__accent {
		flex-shrink: 0;
		width: 4px;
		height: 16px;
		border-radius: 2px;

		.g-block-audio-library--h5 & {
			width: 0.08rem;
			height: 0.32rem;
			border-radius: 0.04rem;
		}
	}

	.g-block-audio-library__title {
		font-size: 16px;
		font-weight: 600;
		line-height: 1.4;

		.g-block-audio-library--h5 & {
			font-size: 0.32rem;
		}
	}

	.g-block-audio-library__list {
		display: flex;
		width: 100%;
	}

	.g-block-audio-library__item {
		flex: 1;
		min-width: 0;
	}

	.g-block-audio-library__cover {
		position: relative;
		margin: 0 auto;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}

		i {
			font-size: 28px;
			color: #bfbfbf;
		}

		.g-block-audio-library--h5 & i {
			font-size: 0.56rem;
		}
	}

	.g-block-audio-library__play {
		position: absolute;
		top: 50%;
		left: 50%;
		z-index: 2;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		margin: -18px 0 0 -18px;
		border-radius: 50%;
		background: rgba(0, 0, 0, 0.45);
		cursor: pointer;
		transition: background 0.2s, transform 0.2s;

		

		.g-block-audio-library--h5 & {
			width: 0.72rem;
			height: 0.72rem;
			margin: -0.36rem 0 0 -0.36rem;
		}
	}

	.g-block-audio-library__triangle {
		display: block;
		width: 0;
		height: 0;
		margin-left: 3px;
		border-top: 6px solid transparent;
		border-bottom: 6px solid transparent;
		border-left: 10px solid #fff;

		.g-block-audio-library__play.is-playing & {
			display: none;
		}

		.g-block-audio-library--h5 & {
			margin-left: 0.06rem;
			border-top-width: 0.12rem;
			border-bottom-width: 0.12rem;
			border-left-width: 0.2rem;
		}
	}

	.g-block-audio-library__pause {
		position: absolute;
		top: 50%;
		left: 50%;
		display: none;
		transform: translate(-50%, -50%);
		gap: 3px;

		.g-block-audio-library__play.is-playing & {
			display: flex;
		}

		.g-block-audio-library--h5 & {
			gap: 0.06rem;
		}
	}

	.g-block-audio-library__pause-bar {
		display: block;
		width: 3px;
		height: 12px;
		background: #fff;
		border-radius: 1px;

		.g-block-audio-library--h5 & {
			width: 0.06rem;
			height: 0.24rem;
			border-radius: 0.02rem;
		}
	}

	.g-block-audio-library__name {
		margin-top: 8px;
		font-size: 12px;
		line-height: 1.4;
		text-align: center;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;

		.g-block-audio-library--h5 & {
			margin-top: 0.16rem;
			font-size: 0.24rem;
		}
	}
</style>
